import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';

import { IOption } from '1_shared/config/interfaces/IOption';
import { ISpecialistByIdData } from '1_shared/config/interfaces/ISpecialistByIdData';
import { Button, Content, Faq, Footer, Page } from '1_shared/ui';
import { AboutSpecialist } from '2_entities';
import {
  Header,
  SpecialistAchievements,
  SpecialistPersonalCard,
  SpecialistPersonBlackCard,
} from '4_widgets';
import SpecialistPersonCardMobile from '../../../4_widgets/SpecialistPersonCard/ui/SpecialistPersonCardMobile';

import useGetDictionaries from '../../../1_shared/api/dictionary/getDictionaries';
import { GetSpec } from '../../../1_shared/api/services';

import styles from './SpecialistPersonPage.module.scss';
import { EYandexPage } from '../../../1_shared/api/services/configs/interfaces/EYandexConfigs';
import { SpecialistAchievementsCardMobile } from '../../../4_widgets/SpecialistAchievementsCard/ui/SpecialistAchievementsCardMobile';
import AboutSpecialistMobile from '../../../2_entities/AboutSpecialistReact/ui/AboutSpecialistMobile';
import SpecialistSpecialization from '../../../3_features/SpecialistSpecialization/SpecialistSpecialization';
import SpecialistReserveSlotMobile from '../../../3_features/SpecialistReserveSlot/ui/SpecialistReserveSlotMobile';
import { ISlot } from '../../../1_shared/config/interfaces/ISlot';
import { RoutePath } from '../../../1_shared/config/routes';
import { useNavigate } from 'react-router';
import { IDescription } from '../../../1_shared/config/interfaces/ISpecialistAchievementsCardProps';
import { IKeyThemeOption } from '1_shared/config/interfaces/IKeyThemesOptions';
import { IWorksWithOption } from '../../../1_shared/config/interfaces/IWorksWithOption';

const SpecialistPersonPage = (): ReactElement => {
  const { specIdentification } = useParams();
  const {
    dictionary,
    dictionaryThemes,
    dictionaryMethods,
    dictionaryWorksWith,
  } = useGetDictionaries();
  const { specData, specDataLoading, specDataError } = GetSpec(
    specIdentification ?? '',
  );
  const navigate = useNavigate();

  const [isMobileVer, setIsMobileVer] = useState(false);

  const methodDescription: Record<string, string> = {};

  const methodDescriptions = useMemo(() => {
    if (!dictionaryMethods || !specData?.workMethods) return [];
    return dictionaryMethods.filter((method: IDescription) =>
      specData?.workMethods.includes(method.id),
    );
  }, [specData, dictionaryMethods]);

  /*
   * Перечень ключевых тем, которые имеются у специалиста,чтобы
   * их можно было передавать в сейт для последующей фильтрации на странице специалистов
   */
  const specialistKeyThemesList: IKeyThemeOption[] = useMemo(
    () =>
      dictionaryThemes?.filter((theme: IKeyThemeOption) =>
        specData?.keyThemes.includes(theme.id),
      ),
    [specData, dictionary, dictionaryThemes],
  );

  /*
   * Перечень "Работает с", которые имеются у специалиста, чтобы использовать их при выводе
   * в блоке "Специализация"
   */
  const specialistWorksWithList: IWorksWithOption[] = useMemo(
    () =>
      dictionaryWorksWith?.filter((item: IWorksWithOption) =>
        specData?.workWiths.includes(item.id),
      ),
    [specData, dictionary, dictionaryWorksWith],
  );

  const specTemp = useMemo(
    () =>
      ({
        ...specData,
        specialities: dictionary
          ? specData?.specialities.map(
              spec =>
                dictionary?.find((option: IOption) => option.id === spec)
                  ?.label ?? '',
            )
          : [],
        workMethods: dictionaryMethods
          ? specData?.workMethods.map(methd => {
              const method = dictionaryMethods?.find(
                (option: IOption) => option.id === methd,
              );

              methodDescription[method.label] = method.description;

              return method.label;
            })
          : [],
        keyThemes: dictionaryThemes
          ? specData?.keyThemes.map(
              themes =>
                dictionaryThemes?.find(
                  (option: IOption) => option.id === themes,
                )?.label ?? '',
            )
          : [],
      }) as ISpecialistByIdData,
    [specData, dictionary, dictionaryMethods, dictionaryThemes],
  );

  const avatar = specData?.mediaContentResponse.find(
    el => el.mediaContentResponse.isPrimary,
  );

  const specCard = useMemo(
    () =>
      ({
        ...specTemp,
        mediaContentResponse: {
          id: avatar?.id,
          ...avatar?.mediaContentResponse,
        },
      }) as any, // ISpecialistData
    [specTemp, specData],
  );

  const onSubmit = (data?: ISlot) => {
    navigate(RoutePath.ORDER, {
      state: { id: specTemp.id, slot: data || specTemp.slots[0] },
    });
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileVer(window.innerWidth <= 768);
    };

    window.addEventListener('resize', checkMobile);
    checkMobile();

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  if (specDataError?.status === 400) {
    return (
      <Page>
        <Header />
        <Content>
          <div className={styles.errorLink}>
            {specDataError?.response?.data?.message}
            <Button type="primary" onClick={() => navigate('/specialists')}>
              ВЕРНУТЬСЯ В КАТАЛОГ
            </Button>
          </div>
        </Content>
      </Page>
    );
  }

  if (specDataLoading) {
    return (
      <Page>
        <Header />
        <Content>
          <Spin indicator={<LoadingOutlined spin />} />
        </Content>
      </Page>
    );
  }

  if (isMobileVer) {
    return (
      <Page>
        <Header />
        <Content wrapperClass={'paddingXNone'}>
          <SpecialistPersonCardMobile spec={specTemp} />
        </Content>
        <Content wrapperClass={'paddingXNone'}>
          <SpecialistSpecialization
            worksWithList={specialistWorksWithList}
            spec={specTemp}
            keyThemesList={specialistKeyThemesList}
          />
        </Content>
        <Content classNames="bg_lightBlue">
          <SpecialistAchievementsCardMobile
            descriptions={methodDescriptions}
            spec={specTemp}
          />
        </Content>
        <AboutSpecialistMobile spec={specTemp} />
        <Content wrapperClass={'paddingXNone'}>
          <SpecialistReserveSlotMobile
            specialistId={specTemp.id}
            spec={specTemp}
            pageType={EYandexPage.Spec}
            onChange={onSubmit}
          />
        </Content>
        <Faq />
        <Footer />
      </Page>
    );
  }

  return (
    <Page>
      <Header />
      <Content wrapperClass={'paddingXNone'}>
        <SpecialistPersonalCard spec={specTemp} />
      </Content>
      <Content wrapperClass={'paddingXNone'}>
        <SpecialistSpecialization
          worksWithList={specialistWorksWithList}
          spec={specTemp}
          keyThemesList={specialistKeyThemesList}
        />
      </Content>
      <Content
        classNames={styles.achievementContent}
        wrapperClass={'paddingXNone'}
      >
        <SpecialistAchievements
          descriptions={methodDescriptions}
          spec={specTemp}
        />
      </Content>
      <Content wrapperClass={'paddingXNone'}>
        <AboutSpecialist spec={specTemp} />
      </Content>
      <Content wrapperClass={'paddingXNone'}>
        <SpecialistPersonBlackCard
          spec={specTemp}
          pageType={EYandexPage.Spec}
        />
      </Content>
      <Content wrapperClass={'paddingXNone'}>
        <Faq />
      </Content>
      <Footer />
    </Page>
  );
};

export default SpecialistPersonPage;
